import {
  Trans,
  useTranslation,
  createFeature,
} from '@edreamsodigeo/frontend-core';
import { ACTIONS, usePFTracking } from '@/hooks/tracking';
import {
  Body,
  Box,
  Heading,
  Link,
  Modal,
  ModalCloseIcon,
} from '@edreamsodigeo/prisma-design-system';
import {
  MoreInfoBannerParag1Subtitle,
  NeedAppSubtitleBoldStyle,
} from './MoreInfoBanner.utils';
import React from 'react';
import { PriceStyle } from '../PriceStyle';
import { PRIME_FIRST_PHASE_2_DESKTOP } from '@/versionedComponents';

export const MoreInfoModalBanner = ({
  days,
  cap,
  deposit,
  closeContent = () => {},
  modalProps = {},
}) => {
  const { t } = useTranslation();
  const { track } = usePFTracking();

  const closeHandler = () => {
    track(ACTIONS.LEARN_MORE.ONCLOSE);
    closeContent();
  };

  return (
    <Modal {...modalProps} size="medium">
      <Box p="1x_small" pb="small">
        <Box p="base">
          <Heading type="2" weight="medium" color="neutral.90">
            {t('pricefreeze.moreinfo.header.title')}
          </Heading>
        </Box>
        <ModalCloseIcon
          onClose={closeHandler}
          sx={{ left: 'unset', right: '16px' }}
          aria-label="closeIcon"
        />
        <MoreInfoBannerDesktop cap={cap} deposit={deposit} days={days} />
      </Box>
    </Modal>
  );
};

const MoreInfoBannerNeedAppParag = ({ days }) => {
  const { t } = useTranslation();
  return (
    <Box pb="base">
      <Box pb="1x_small">
        <Heading type="3" weight="medium" color="neutral.90">
          {t('pricefreeze.moreinfo.needapp.title')}
        </Heading>
      </Box>
      <Box pb="2x_small">
        <Body weight="regular" color="neutral.90" lineHeight="24px">
          <Trans
            i18nKey="pricefreeze.moreinfo.needapp.trip.subtitle.normal"
            components={{
              bold: (
                <NeedAppSubtitleBoldStyle
                  type="base"
                  content={t('pricefreeze.moreinfo.needapp.subtitle.bold')}
                />
              ),
              days: `${days}`,
            }}
          />
        </Body>
      </Box>
    </Box>
  );
};

const MoreInfoBannerBookIfWantParag = () => {
  const { t } = useTranslation();
  return (
    <Box pb="base">
      <Box pb="1x_small">
        <Heading type="3" weight="medium" color="neutral.90">
          {t('pricefreeze.primefirst.moreinfo.book.title')}
        </Heading>
      </Box>
      <Box pb="2x_small">
        <Body weight="regular" color="neutral.90" lineHeight="24px">
          {t('pricefreeze.primefirst.moreinfo.book.subtitle')}
        </Body>
      </Box>
    </Box>
  );
};

const MoreInfoBannerPrimeParag = ({ onTrack }) => {
  const { t } = useTranslation();
  return (
    <Box pb="1x_big">
      <Box pb="1x_small">
        <Heading type="3" weight="medium" color="neutral.90">
          {t('moreinfo.subscription.valueprop.title')}
        </Heading>
      </Box>
      <Box>
        <Body weight="regular" color="neutral.90" lineHeight="24px">
          {t('moreinfo.subscription.valueprop.subtitle')}
        </Body>
        <Body
          ml="big"
          weight="regular"
          color="neutral.90"
          sx={{ whiteSpace: 'nowrap' }}
          inline
          onClick={() => onTrack(ACTIONS.LEARN_MORE.CLICK.PRIME)}
        >
          <Link href={t('terms.prime.url')} target={'_blank'} type={'dotted'}>
            {t('pricefreeze.moreinfo.subscription.subtitle.link')}
          </Link>
        </Body>
      </Box>
    </Box>
  );
};

const MoreInfoBannerParag2Versioned = createFeature({
  name: PRIME_FIRST_PHASE_2_DESKTOP,
  variations: [
    MoreInfoBannerNeedAppParag,
    MoreInfoBannerBookIfWantParag,
    MoreInfoBannerBookIfWantParag,
  ],
  version: 1,
});

const MoreInfoBannerParag3Versioned = createFeature({
  name: PRIME_FIRST_PHASE_2_DESKTOP,
  variations: [
    MoreInfoBannerPrimeParag,
    MoreInfoBannerNeedAppParag,
    MoreInfoBannerNeedAppParag,
  ],
  version: 1,
});

export const MoreInfoBannerDesktop = ({ days, cap, deposit }) => {
  const { t } = useTranslation();
  const { track } = usePFTracking();

  return (
    <>
      <Box px="base" pt="base">
        <Box pb="base">
          <Box pb="1x_small">
            <Heading type="3" weight="medium" color="neutral.90">
              <Trans
                i18nKey="pricefreeze.moreinfo.coverage.title"
                components={{
                  price: <PriceStyle money={cap} rounded />,
                }}
              />
            </Heading>
          </Box>
          <Box pb="2x_small">
            <Body weight="regular" color="neutral.90" lineHeight="24px">
              <MoreInfoBannerParag1Subtitle
                cap={cap}
                deposit={deposit}
                days={days}
              />
            </Body>
          </Box>
        </Box>

        <MoreInfoBannerParag2Versioned days={days} />
        <MoreInfoBannerParag3Versioned onTrack={track} days={days} />

        <Box pb="1x_big" sx={{ textAlign: 'center' }}>
          <Body
            inline
            weight="regular"
            onClick={() => track(ACTIONS.LEARN_MORE.CLICK.TERMS)}
          >
            <Link
              href={t('terms.pricefreeze.url')}
              target={'_blank'}
              type={'dotted'}
            >
              {t('pricefreeze.moreinfo.termsandconditions.link')}
            </Link>
          </Body>
        </Box>
      </Box>
    </>
  );
};
